import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import {
  useRequest,
  type MutationFunction,
  type QueryFunction,
} from '../common';
import {
  getApiMemberDetailsKey,
  getApiMembersListKey,
  getManagedIntegrationListKey,
} from './keys';

//Fetches entire list of available API members
export const useApiMembersList: QueryFunction<
  any[],
  [managedIntegrationUuid: string],
  ReturnType<typeof getApiMembersListKey>
> = (managedIntegrationUuid, { query, requestOptions, queryOptions } = {}) => {
  const request = useRequest(requestOptions);

  return useQuery({
    ...queryOptions,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps -- Fix in PP-24289
    queryKey: getApiMembersListKey(),
    queryFn: ({ signal }) =>
      request(`/managed_integrations/${managedIntegrationUuid}/api_members`, {
        query,
        init: { signal },
      }),
  });
};

//This creates an API member, overwrites QueryData for that individual member,
//and invalidates all related API member list queries
export const useCreateApiMember: MutationFunction<
  unknown,
  { uuid: string; body: any }
> = ({ query, requestOptions, queryOptions } = {}) => {
  const queryClient = useQueryClient();
  const request = useRequest(requestOptions);

  return useMutation({
    mutationFn: ({ uuid, body }) => {
      return request(`/managed_integrations/${uuid}/api_members`, {
        query,
        init: { method: 'POST', body },
      });
    },
    ...queryOptions,
    mutationKey: ['createApiMember'],
    onSuccess: (data, requestData, ...rest) => {
      const key = getApiMemberDetailsKey(data?.id);
      queryClient.setQueryData(key, data);
      queryClient.invalidateQueries({ queryKey: getApiMembersListKey() });
      queryOptions?.onSuccess?.(data, requestData, ...rest);
    },
  });
};

//This creates an API member, overwrites QueryData for that individual member,
//and invalidates all related API member list queries
export const useCreateDefaultApiMember: MutationFunction<unknown, never> = ({
  query,
  requestOptions,
  queryOptions,
} = {}) => {
  const queryClient = useQueryClient();
  const request = useRequest(requestOptions);

  return useMutation({
    mutationFn: () => {
      return request(`/integrations/api_token`, {
        query,
        init: { method: 'POST' },
      });
    },
    ...queryOptions,
    mutationKey: ['createDefaultApiMember'],
    onSuccess: (data, requestData, ...rest) => {
      //   const key = getApiMemberDetailsKey(data.id);
      //   queryClient.setQueryData(key, data);
      queryClient.invalidateQueries({ queryKey: getApiMembersListKey() });
      queryClient.invalidateQueries({
        queryKey: getManagedIntegrationListKey(),
      });
      queryOptions?.onSuccess?.(data, requestData, ...rest);
    },
  });
};

//This creates an API member and sets it to the primary token, while setting
//the previous token to secondary. It then sets the previous token to expire in the
//provided time frame.
export const useCycleApiMember: MutationFunction<
  unknown,
  { uuid: string; body: any }
> = ({ query, requestOptions, queryOptions } = {}) => {
  const queryClient = useQueryClient();
  const request = useRequest(requestOptions);

  return useMutation({
    mutationFn: ({ uuid, body }) => {
      return request(`/managed_integrations/${uuid}/cycle_api_tokens`, {
        query,
        init: { method: 'POST', body },
      });
    },
    ...queryOptions,
    mutationKey: ['cycleApiMember'],
    onSuccess: (data, requestData, ...rest) => {
      const key = getApiMemberDetailsKey(data?.id);
      queryClient.setQueryData(key, data);
      queryClient.invalidateQueries({ queryKey: getApiMembersListKey() });
      queryOptions?.onSuccess?.(data, requestData, ...rest);
    },
  });
};

//This edits an API member, overwrites QueryData for that individual member,
//and invalidates all related API member list queries
export const useEditApiMember: MutationFunction<
  unknown,
  { uuid: string; id: string; body: any }
> = ({ query, requestOptions, queryOptions } = {}) => {
  const queryClient = useQueryClient();
  const request = useRequest(requestOptions);

  return useMutation({
    mutationFn: ({ uuid, id, body }) => {
      return request(`/managed_integrations/${uuid}/api_members/${id}`, {
        query,
        ...requestOptions,
        init: { method: 'PATCH', body },
      });
    },
    ...queryOptions,
    mutationKey: ['editApiMember'],
    onSuccess: (data, requestData, ...rest) => {
      const key = getApiMemberDetailsKey(requestData.id);
      queryClient.setQueryData(key, data);
      queryClient.invalidateQueries({ queryKey: getApiMembersListKey() });
      queryOptions?.onSuccess?.(data, requestData, ...rest);
    },
  });
};

//This deletes an API member, overwrites QueryData for that individual member,
//and invalidates all related API member list queries
export const useDeleteApiMember: MutationFunction<
  unknown,
  { uuid: string; id: string }
> = ({ query, requestOptions, queryOptions } = {}) => {
  const queryClient = useQueryClient();
  const request = useRequest(requestOptions);

  return useMutation({
    mutationFn: ({ uuid, id }) => {
      return request(`/managed_integrations/${uuid}/api_members/${id}`, {
        query,
        init: { method: 'DELETE' },
      });
    },
    ...queryOptions,
    mutationKey: ['deleteApiMember'],
    onSuccess: (data, requestData, ...rest) => {
      const key = getApiMemberDetailsKey(requestData.id);
      queryClient.setQueryData(key, data); // Do we need this?
      queryClient.invalidateQueries({ queryKey: getApiMembersListKey() });
      queryOptions?.onSuccess?.(data, requestData, ...rest);
    },
  });
};

export default {
  useApiMembersList,
  useCreateApiMember,
  useCreateDefaultApiMember,
  useCycleApiMember,
  useEditApiMember,
  useDeleteApiMember,
};
